<template>
    <div>
        <div class="header-with-tiles">
            <div class="email-page-header">
                <div class="title-arrow">
                    <ds-text-button gray as="a" href="/Dashboard/userDashboard.jsp">
                        <ds-icon name="arrow-left" />
                    </ds-text-button>

                    <div>Email engagement</div>
                </div>

                <a class="feedback-link" @click="isFeedbackModalOpen=true">
                    <ds-icon name="message" />
                    <div>
                        Leave feedback
                    </div>
                </a>

                <share-feedback-modal
                    :is-open="isFeedbackModalOpen"
                    :feature="$options.EMAIL_ANALYTICS"
                    :slack-channels="[$options.SLACK_CHANNELS.IN_APP_FEEDBACK, $options.SLACK_CHANNELS.EMAIL_ANALYTICS_FEEDBACK]"
                    @close="isFeedbackModalOpen = false"
                />
            </div>

            <ds-inline-alert
                v-if="showMessage"
                leading-icon
                type="critical"
                class="alert-banner"
            >
                {{ getAlertBannerMessage }}
                <ds-inline-alert-footer-link
                    v-if="showAlertBannerLink"
                    href="https://help.infusionsoft.com/help/contact-support"
                >
                    Contact Support
                </ds-inline-alert-footer-link>
            </ds-inline-alert>

            <div class="engagement-details-page">
                <ds-card class="card">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Average email engagement
                        </div>

                        <div />

                        <div class="last-30-days">
                            Last 30 days
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <progress-bar-pi-chart-card
                            :data-by-type="dataTypesWithColors"
                            :progress-bar-value="averageEngagementDays"
                            :progress-bar-upper-limit="averageEngagementDaysUpperLimit"
                            :progress-bar-healthy-limit="averageEngagementDaysHealthyLimit"
                            :progress-bar-units="' days'"
                            :sorted-data="false"
                            :link-label="'View details'"
                            link-href="#engagement-report"
                            :empty-state-message="'No email engagement has been recorded'"
                        />
                    </ds-card-body>
                </ds-card>

                <description-card
                    class="card"
                    header-text="What is email engagement?"
                >
                    <template v-slot:content>
                        <div v-if="isEmailHealthContentUpdateEnabled">
                            <p>Email engagement refers to how recently and frequently your contacts interact with your emails — specifically, how many days have passed since they last opened or clicked an email sent from your Keap app.</p>

                            <span><strong>What does a "highly engaged" contact look like? </strong> A contact is considered highly engaged if they have:</span>
                            <br />
                            <ul class="ul">
                                <li>Opened at least one of your emails within the past 30 days, or</li>
                                <li>Clicked a link in any of your emails within the past 30 days</li>
                            </ul>

                            <p>
                                Why is engagement so important? Because inbox providers (like Gmail and Outlook) are smarter than ever. They've moved beyond just blocking obvious spam — now they're filtering out emails that don’t get interaction. If your audience isn’t opening or clicking, your messages could start landing in the spam folder.
                            </p>
                            <p>
                                Bottom line: consistently engaging your contacts isn’t just a nice-to-have — it’s a must if you want to reach the inbox.
                            </p>
                        </div>

                        <div v-else>
                            <p>Email engagement is the time (in days) between when a customer last opens/clicks an email and when you send a new email via your Keap app.</p>

                            <span><strong>A highly engaged contact:</strong></span>
                            <br />
                            <ul class="ul">
                                <li>Has opened email you've sent within 30 days</li>
                                <li>Or has clicked a link in any email you've sent within 30 days</li>
                            </ul>

                            <p>
                                Getting your customers to engage with your emails is important. Why? Because spam filters are getting smarter. They've mastered filtering out blatant spam and have now evolved to
                                <strong>filter out emails that recipients aren't engaging with. </strong>
                                Think about that: just by not engaging with your email, a recipient can land you in spam city.
                            </p>
                        </div>
                    </template>
                </description-card>

                <ds-card :class="getClass()">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Take action to improve email engagement
                        </div>

                        <div class="last-30-days">
                            Recommended actions
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <engagement-take-action-checklist-card
                            :is-update-help-article-links-enabled="isUpdateHelpArticleLinksEnabled"
                            :is-unengaged-email-status-processing-enabled="isUnengagedEmailStatusProcessingEnabled"
                            :app-edition="appEdition"
                        />
                    </ds-card-body>
                </ds-card>

                <EmailRehabServiceCard
                    v-if="displayRehabServiceCard"
                    class="card"
                    icon-path="/resources/svg/customer-support.svg"
                    :header-text="$t('email.healthWidget.rehabServiceTile.engagement.headerText')"
                    :body-text="$t('email.healthWidget.rehabServiceTile.engagement.bodyText')"
                    :link-address="linkAddress"
                    :help-button-label="$t('email.healthWidget.rehabServiceTile.engagement.helpButtonLabel')"
                />
            </div>
        </div>

        <div class="engagement-report-container">
            <engagement-report id="engagement-report" :timezone="timezone" />
        </div>
    </div>
</template>

<script>
import ProgressBarPiChartCard from './ProgressBarPiChartCard';
import DescriptionCard from './shared/DescriptionCard';
import EngagementReport from './EngagementReport';
import EngagementTakeActionChecklistCard from './EngagementTakeActionChecklistCard';
import axios from 'axios';
import { complianceThresholds } from '@/utils/email.util';
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal';
import { EMAIL_ANALYTICS } from '@/components/shared/constants/shareFeedback.constants';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import EmailRehabServiceCard from './shared/EmailRehabServiceCard';

const ENGAGEMENT_ITEM_COLORS = [
    '#0040D8',
    '#8BDC00',
    '#36DFFF',
    '#FFC400',
    '#F44366',
];
const ENGAGEMENT_HOVER_LABELS = [
    'Never engaged',
    '0-30 days',
    '31-60 days',
    '61-120 days',
    '>120 days',
];


export default {
    EMAIL_ANALYTICS,
    SLACK_CHANNELS,

    components: {
        EmailRehabServiceCard,
        DescriptionCard,
        ProgressBarPiChartCard,
        EngagementReport,
        EngagementTakeActionChecklistCard,
        ShareFeedbackModal,
    },

    props: {
        appId: String,
        appEdition: String,
        isEmailRehabServiceEnabled: {
            type: Boolean,
            default: false,
        },
        firstName: String,
        lastName: String,
        timezone: String,
        phoneNumber: String,
        email: String,
        isEmailHealthContentUpdateEnabled: {
            type: Boolean,
            default: false,
        },
        isUpdateHelpArticleLinksEnabled: {
            type: Boolean,
            default: false,
        },
        isUnengagedEmailStatusProcessingEnabled: Boolean,
        isEmailLockedDown: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            errorLoading: false,
            averageEngagementDays: 0,
            averageEngagementDaysByType: [],
            alertBannerMessage: '',
            averageEngagementDaysUpperLimit: null,
            averageEngagementDaysHealthyLimit: null,
            isFeedbackModalOpen: false,
        };
    },

    created() {
        this.load();
    },

    computed: {
        displayRehabServiceCard() {
            return this.isEmailRehabServiceEnabled && this.averageEngagementDays > this.averageEngagementDaysUpperLimit;
        },

        linkAddress() {
            return `https://keap.com/email-deliverability-consultation?appId=${this.appId}&firstName=${this.firstName}&lastName=${this.lastName}&email=${this.email}&phone=${this.phoneNumber}`;
        },

        error_pulling_data () {
            return this.loading === false && this.errorLoading === true;
        },
        dataTypesWithColors() {
            return this.averageEngagementDaysByType?.map((entity, index) => {
                return {
                    type: entity.type,
                    number: entity.number,
                    color: ENGAGEMENT_ITEM_COLORS[index % ENGAGEMENT_ITEM_COLORS.length],
                    legendHoverLabel: ENGAGEMENT_HOVER_LABELS[index % ENGAGEMENT_HOVER_LABELS.length],
                };
            });
        },
        getAlertBannerMessage() {
            if (this.isEmailLockedDown) {
                return 'Due to a compliance issue your ability to send email broadcasts has been disabled. '
                        + 'Contact support for details on how to re-enable broadcast sending.';
            }

            if (this.error_pulling_data) {
                return 'An issue was encountered while retrieving your data. Please try reloading the page.';
            }

            if (this.averageEngagementDays > complianceThresholds.averageEngagementDaysHealthyLimit) {
                return 'You are about to exceed the engagement limit. '
                        + 'If you do not take action soon, you may fall out of Keap’s Email Compliance Policy and if so, email sending restrictions may occur.';
            }

            return 'Your email engagement is good.';
        },

        showAlertBannerLink() {
            return this.isEmailLockedDown;
        },

        showMessage() {
            return this.error_pulling_data
                    || this.isEmailLockedDown;
        },

    },

    methods: {
        getClass() {
            if (this.displayRehabServiceCard) {
                return 'card';
            }

            return 'single-line-card';
        },

        async load() {
            await this.loadMetrics();
        },

        async loadMetrics() {
            try {
                const metricsResponse = await axios.get('/app/metrics/getEngagementPageMetrics');

                this.averageEngagementDays = metricsResponse.data.emailHealthMetrics.averageEngagementDays;
                const engagementData = metricsResponse.data.emailEngagementCounts;

                // Order matters as we are zipping this list with other lists in dataTypesWithColors()
                this.averageEngagementDaysByType = [
                    {
                        type: 'Sent to, never engaged',
                        number: engagementData.contactsNeverEngaged,
                    },
                    {
                        type: 'Highly engaged',
                        number: engagementData.contactsEngagedBetween0And30Days,
                    },
                    {
                        type: 'Engaged',
                        number: engagementData.contactsEngagedBetween31And60Days,
                    },
                    {
                        type: 'Low engagement',
                        number: engagementData.contactsEngagedBetween61And120Days,
                    },
                    {
                        type: 'Unengaged',
                        number: engagementData.contactsEngagedAbove120Days,
                    },
                ];

                this.averageEngagementDaysUpperLimit = complianceThresholds.averageEngagementDaysUpperLimit;
                this.averageEngagementDaysHealthyLimit = complianceThresholds.averageEngagementDaysHealthyLimit;

                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.errorLoading = true;
            }
        },
    },

};

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.alert-banner {
    display: flex;
}

.ul {
    list-style: disc;
}

.engagement-details-page {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
}

.engagement-report-container {
    margin: auto;
    width: 90vw;
}

.email-page-header {
    display: flex;
    align-items: center;
    padding-bottom: 0;
}

.title-arrow {
    font-size: $font-size-jumbo;
    align-items: center;
    flex: 50%;
    display: flex;
}

.header-with-tiles {
    max-width: 72rem;
    margin: auto;
}

.card {
    margin: $spacing-100;
    width: calc(50% - 1.5em);
}

.single-line-card {
    margin: $spacing-100;
    width: calc(100% - 1.5em);
}

.email-card-header {
    flex: 50%;
    float: left;
    font-size: $font-size-lg;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
}

.last-30-days {
    font-size: $font-size-xs;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
    text-align: left;
}

.feedback-link {
    display: flex;
    font-size: $font-size-sm;
    align-items: center;
    color: $color-blue-700;
}

.card-title {
    display: flex;
    flex-direction: column;
    border-bottom: solid px-to-rem(1) $color-ink-200;
    padding-bottom: $spacing-100;
}

</style>
